import {
  AudienceType,
  AdminEvent,
  EventType,
  TicketType,
} from "Types/Admin/AdminEventType";
import { ApiService } from "./ApiService";
import { ResultsDTO } from "Types/ResultsDTO";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const getPlant = (plant?: string) => {
  const resolvedPlant = plant || process.env.REACT_APP_PLANT;
  if (!resolvedPlant) {
    console.error("❌ PLANT is missing in Event API call");
  }
  return resolvedPlant;
};

export const EventApiSlice = ApiService.injectEndpoints({
  endpoints: (builder) => ({
    getAllEvents: builder.query<AdminEvent[], { plant?: string; search?: string }>({
      query: (params) => {
        const plant = getPlant(params.plant);
        return {
          url: `/event/getAll`,
          method: "GET",
          params: {
            plant,
            ...(params.search && { search: params.search }),
          },
        };
      },
      transformResponse: (response: ResultsDTO<any[]>) => {
        const eventsArray = response?.results ?? [];

        const parseDate = (iso: string) => {
          if (!iso) return "";
          const d = dayjs(iso);
          return d.isValid() ? d.format("DD/MM/YYYY") : "";
        };

        const parseTime = (iso: string) => {
          if (!iso) return "";
          const d = dayjs(iso);
          return d.isValid() ? d.format("HH:mm") : "";
        };

        return eventsArray.map((eventObj: any) => {
          const event = eventObj.eventHdr || {};
          const eventDet = eventObj.eventDet || [];

          const eventDate = parseDate(event.eventDate);
          const eventStartTime = parseTime(event.eventStartTime);
          const eventEndTime = parseTime(event.eventEndTime);

          const eventEndDate = event.eventEndDate
            ? parseDate(event.eventEndDate)
            : parseDate(event.eventDate);

          const imageDetails = eventDet
            .map((det: any) => ({ id: det.id, url: det.eventImage }))
            .filter((d: any) => d.url && d.url.trim() !== "");

          return {
            id: event.id,
            name: event.eventName,
            description: event.eventDescription,
            startDate: `${eventDate} ${eventStartTime}`,
            endDate: `${eventEndDate} ${eventEndTime}`,
            venue: event.eventAddress,
            audience: event.audienceType
              ? event.audienceType.split(",").map((a: string) => a.trim() as AudienceType)
              : [],
            eventName: event.eventName,
            eventDescription: event.eventDescription,
            eventType: (event.eventType as EventType) || "Online",
            eventDate,
            eventStartTime,
            eventEndTime,
            eventEndDate,
            eventAddress: event.eventAddress,
            eventCity: event.eventCity,
            ticketType: (event.ticketType as TicketType) || "Free",
            audienceType: event.audienceType,
            plant: event.plant,
            createdAt: event.crAt,
            imageUrls: imageDetails.map((d: any) => d.url),
            imageDetails,
          };
        });
      },
      providesTags: ["Event"],
    }),

    getEventById: builder.query<AdminEvent, { id: number; plant?: string }>({
      query: (params) => {
        const plant = getPlant(params.plant);
        return {
          url: `/event`,
          method: "GET",
          params: { id: params.id, plant },
        };
      },
      transformResponse: (response: ResultsDTO<any>) => {
        const eventObj = response?.results ?? {};
        const event = eventObj.eventHdr || {};
        const eventDet = eventObj.eventDet || [];

        const parseDate = (iso: string) => {
          if (!iso) return "";
          const d = dayjs(iso);
          return d.isValid() ? d.format("DD/MM/YYYY") : "";
        };

        const parseTime = (iso: string) => {
          if (!iso) return "";
          const d = dayjs(iso);
          return d.isValid() ? d.format("HH:mm") : "";
        };

        const eventDate = parseDate(event.eventDate);
        const eventStartTime = parseTime(event.eventStartTime);
        const eventEndDate = parseDate(event.eventEndDate);
        const eventEndTime = parseTime(event.eventEndTime);

        const imageDetails = eventDet
          .map((det: any) => ({ id: det.id, url: det.eventImage }))
          .filter((d: any) => d.url && d.url.trim() !== "");

        return {
          id: event.id,
          name: event.eventName,
          description: event.eventDescription,
          startDate: `${eventDate} ${eventStartTime}`,
          endDate: `${eventEndDate} ${eventEndTime}`,
          venue: event.eventAddress,
          audience: event.audienceType
            ? event.audienceType.split(",").map((a: string) => a.trim() as AudienceType)
            : [],
          eventName: event.eventName,
          eventDescription: event.eventDescription,
          eventType: event.eventType === "In-Person" ? "In-Person" : "Online",
          eventDate,
          eventStartTime,
          eventEndTime,
          eventEndDate,
          eventAddress: event.eventAddress,
          eventCity: event.eventCity,
          ticketType: event.ticketType === "FREE" ? "Free" : "Chargeable",
          audienceType: event.audienceType,
          plant: event.plant,
          createdAt: event.crAt,
          imageUrls: imageDetails.map((d: any) => d.url),
          imageDetails,
        };
      },
      providesTags: (result, error, arg) => [{ type: "Event", id: arg.id }],
    }),

    createEvent: builder.mutation<ResultsDTO, FormData>({
      query: (formData) => ({
        url: `/event/create`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),

    updateEvent: builder.mutation<ResultsDTO, any>({
      query: (payload) => ({
        url: `/event/update`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => {
        let eventId: number | undefined;
        if (arg instanceof FormData) {
          const dataString = arg.get("data");
          if (typeof dataString === "string") {
            try {
              const parsed = JSON.parse(dataString);
              eventId = parsed?.id;
            } catch {
              // ignore parse failures
            }
          }
        } else if (arg && typeof arg === "object") {
          eventId = (arg as any).id;
        }
        return eventId ? [{ type: "Event", id: eventId }, "Event"] : ["Event"];
      },
    }),

    deleteEvent: builder.mutation<ResultsDTO, { id: number; plant?: string }>({
      query: (params) => {
        const plant = getPlant(params.plant);
        return {
          url: `/event/delete`,
          method: "DELETE",
          params: { id: params.id, plant },
        };
      },
      invalidatesTags: ["Event"],
    }),

    deleteEventImage: builder.mutation<ResultsDTO, { detId: number; eventId: number; plant?: string }>({
      query: ({ detId, plant }) => {
        const resolvedPlant = getPlant(plant);
        return {
          url: `/event/delete-image`,
          method: "DELETE",
          params: { detId, plant: resolvedPlant },
        };
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: "Event", id: eventId }],
    }),

    uploadEventImage: builder.mutation<ResultsDTO, { plant: string; hdrId: number; files: File[] }>({
      query: ({ plant, hdrId, files }) => {
        const formData = new FormData();
        formData.append("plant", plant);
        formData.append("hdrId", hdrId.toString());
        files.forEach((file) => formData.append("files", file));
        return {
          url: `/event/upload-image`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { hdrId }) => [{ type: "Event", id: hdrId }],
    }),

    bulkDeleteEvents: builder.mutation<ResultsDTO, { ids: number[]; plant?: string }>({
      query: (params) => {
        const plant = getPlant(params.plant);
        return {
          url: `/event/bulk-delete`,
          method: "DELETE",
          params: { ids: params.ids, plant },
        };
      },
      invalidatesTags: ["Event"],
    }),
  }),
});

export const {
  useGetAllEventsQuery,
  useLazyGetAllEventsQuery,
  useGetEventByIdQuery,
  useLazyGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useDeleteEventImageMutation,
  useUploadEventImageMutation,
  useBulkDeleteEventsMutation,
} = EventApiSlice;
